// lib/leasing/analysisGroups.ts
//
// The scored analysis of a shortlist row, as data. One rubric run produces criteria with scores and
// reasoning, the questions it asked (`strategy`) and the facts it stood on (`rawFacts`); a Lead and
// a PortfolioFit carry the same three fields and used to fold them into a table each in its own way.
// This is that fold, once, with no JSX in it so it can be tested.
//
// Evidence is OPTIONAL by construction: a row scored before its table carried the evidence columns
// has none. So the shell asks this module WHICH columns actually carry data instead of rendering
// five columns of which three are permanently empty.

import { criterionTitle } from '#lib/leasing/criterionMatch';
import type {
  AnalysisColumnId,
  AtomicFact,
  CriterionEvidenceBucket,
  CriterionGroup,
  RenderableCriterionGroup,
  SearchIntent,
  SourcingCriterionResult,
} from '#types/types';

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * Facts from several sources, deduplicated on claim + quote + url. Earlier sources win, which is
 * how a row's own evidence keeps precedence over the evidence it inherits from its parent.
 */
export const mergeAtomicFacts = (...sources: Array<AtomicFact[] | undefined>): AtomicFact[] => {
  const seen = new Set<string>();
  const merged: AtomicFact[] = [];

  sources.forEach((facts) => {
    if (!Array.isArray(facts)) return;
    facts.forEach((fact) => {
      const key = `${trimmed(fact?.claim).toLowerCase()}|${trimmed(fact?.exactQuote).toLowerCase()}|${trimmed(fact?.sourceUrl).toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(fact);
    });
  });

  return merged;
};

const criterionMergeKey = (criterion: SourcingCriterionResult, fallback: string): string => {
  const criterionId = trimmed(criterion?.criterionId).toLowerCase();
  if (criterionId.length > 0) return `id:${criterionId}`;
  const label = trimmed(criterion?.label).toLowerCase();
  if (label.length > 0) return `label:${label}`;
  return fallback;
};

/**
 * Criteria from several sources folded on their id (or their label when the id is missing), in
 * first-source order. Earlier sources win field by field, so a criterion the row scored itself keeps
 * its score and its reasoning, and only fills its holes from the inherited one.
 */
export const mergeCriteriaResults = (
  ...sources: Array<SourcingCriterionResult[] | undefined>
): SourcingCriterionResult[] => {
  const byKey = new Map<string, SourcingCriterionResult>();
  const order: string[] = [];

  sources.forEach((criteria, sourceIndex) => {
    if (!Array.isArray(criteria)) return;
    criteria.forEach((criterion, index) => {
      const key = criterionMergeKey(criterion, `${sourceIndex}:${index}`);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, criterion);
        order.push(key);
        return;
      }

      const citations = Array.from(
        new Set(
          [
            ...(Array.isArray(existing.citations) ? existing.citations : []),
            ...(Array.isArray(criterion.citations) ? criterion.citations : []),
          ]
            .map(trimmed)
            .filter((citation) => citation.length > 0)
        )
      );

      byKey.set(key, {
        ...criterion,
        ...existing,
        criterionId: existing.criterionId || criterion.criterionId,
        label: existing.label || criterion.label,
        score:
          typeof existing.score === 'number' && Number.isFinite(existing.score)
            ? existing.score
            : criterion.score,
        maxScore:
          typeof existing.maxScore === 'number' && Number.isFinite(existing.maxScore)
            ? existing.maxScore
            : criterion.maxScore,
        reasoning: trimmed(existing.reasoning) || trimmed(criterion.reasoning),
        citations,
        facts: mergeAtomicFacts(existing.facts, criterion.facts),
      });
    });
  });

  return order
    .map((key) => byKey.get(key))
    .filter((criterion): criterion is SourcingCriterionResult => Boolean(criterion));
};

/**
 * One row group per scored criterion, each carrying the questions asked for it and the facts found.
 *
 * When nothing is scorable yet but the run has already asked or found something, a single "pending
 * verdict" group carries it: a row mid-run shows what it is looking for instead of an empty table.
 * When there is neither a verdict nor evidence, the result is empty and the caller renders nothing.
 */
export const buildCriterionGroups = ({
  criteria,
  strategy,
  rawFacts,
  idPrefix = '',
  pendingLabel,
}: {
  criteria: SourcingCriterionResult[] | undefined;
  strategy: SearchIntent[] | undefined;
  rawFacts: AtomicFact[] | undefined;
  /** Namespaces the React keys when a page shows more than one run. */
  idPrefix?: string;
  pendingLabel: string;
}): CriterionGroup[] => {
  // Both fields come straight out of a JSONB column, so neither is guaranteed.
  const inquiriesByCriterion = new Map<string, SearchIntent[]>();
  (Array.isArray(strategy) ? strategy : []).forEach((intent) => {
    if (!intent?.criterionId || typeof intent.question !== 'string') return;
    const bucket = inquiriesByCriterion.get(intent.criterionId) ?? [];
    bucket.push(intent);
    inquiriesByCriterion.set(intent.criterionId, bucket);
  });

  const groups: CriterionGroup[] = (Array.isArray(criteria) ? criteria : []).map((criterion) => ({
    id: `${idPrefix}${criterion.criterionId}`,
    // Rubric labels carry their definition after the name, so only the head shows and the full
    // text stays for the tooltip and the search.
    label: criterionTitle(criterion.label),
    labelFull: criterion.label,
    score: criterion.score,
    maxScore: criterion.maxScore,
    reasoning: criterion.reasoning,
    facts: Array.isArray(criterion.facts) ? criterion.facts : [],
    inquiries: inquiriesByCriterion.get(criterion.criterionId) ?? [],
  }));

  const hasVerdict = groups.some(
    (group) => typeof group.score === 'number' || trimmed(group.reasoning).length > 0
  );
  if (hasVerdict) return groups;

  const pendingFacts = Array.isArray(rawFacts) ? rawFacts : [];
  const pendingInquiries = Array.isArray(strategy) ? strategy.filter((intent) => typeof intent?.question === 'string') : [];
  if (pendingFacts.length === 0 && pendingInquiries.length === 0) return [];

  return [
    {
      id: `${idPrefix}_pending`,
      label: pendingLabel,
      labelFull: pendingLabel,
      facts: pendingFacts,
      inquiries: pendingInquiries,
    },
  ];
};

/** True when at least one group has something to show in the detailed view. */
export const hasAnalysisEvidence = (groups: CriterionGroup[]): boolean =>
  groups.some((group) => group.facts.length > 0 || group.inquiries.length > 0);

/**
 * The columns that actually carry data, in table order. A run with no evidence yields
 * ['score', 'criterion', 'facts'] and the detailed table stops rendering three empty columns.
 */
export const analysisColumnsWithData = (groups: CriterionGroup[]): AnalysisColumnId[] => {
  if (groups.length === 0) return [];
  const columns: AnalysisColumnId[] = [];
  if (groups.some((group) => typeof group.score === 'number')) columns.push('score');
  columns.push('criterion');
  if (groups.some((group) => group.inquiries.length > 0)) columns.push('inquiries');
  // The facts cell falls back to the reasoning, so a scored run with no facts still fills it.
  if (groups.some((group) => group.facts.length > 0 || trimmed(group.reasoning).length > 0)) {
    columns.push('facts');
  }
  if (groups.some((group) => group.facts.some((fact) => fact.isInternal || trimmed(fact.sourceUrl).length > 0))) {
    columns.push('links');
  }
  return columns;
};

const factMatches = (fact: AtomicFact, query: string): boolean =>
  trimmed(fact.claim).toLowerCase().includes(query) ||
  trimmed(fact.sourceUrl).toLowerCase().includes(query) ||
  trimmed(fact.exactQuote).toLowerCase().includes(query);

const inquiryMatches = (inquiry: SearchIntent | undefined, query: string): boolean =>
  typeof inquiry?.question === 'string' && inquiry.question.toLowerCase().includes(query);

/**
 * Groups split into one bucket per question, facts spread evenly over them, then filtered by the
 * search query.
 *
 * There is no recorded link from a fact to the question that produced it, so the split is
 * positional, and empty buckets are KEPT: a question the run asked and found nothing on is part of
 * the record. A criterion whose name matches the query stays whole, facts or no facts, because
 * dropping it for having none hides exactly the criteria the user is looking for.
 */
export const filterCriterionGroups = (
  groups: CriterionGroup[],
  rawQuery: string
): RenderableCriterionGroup[] => {
  const query = rawQuery.trim().toLowerCase();

  return groups.flatMap((group) => {
    let buckets: CriterionEvidenceBucket[] =
      group.inquiries.length === 0
        ? [{ facts: group.facts }]
        : group.inquiries.map((inquiry, index) => ({
            inquiry,
            facts: group.facts.slice(
              Math.floor((index * group.facts.length) / group.inquiries.length),
              Math.floor(((index + 1) * group.facts.length) / group.inquiries.length)
            ),
          }));

    if (query && !group.labelFull.toLowerCase().includes(query)) {
      buckets = buckets
        .map((bucket) =>
          inquiryMatches(bucket.inquiry, query)
            ? bucket
            : { ...bucket, facts: bucket.facts.filter((fact) => factMatches(fact, query)) }
        )
        .filter((bucket) => bucket.facts.length > 0 || inquiryMatches(bucket.inquiry, query));
      if (buckets.length === 0) return [];
    }

    return [{ ...group, buckets }];
  });
};
